import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, MapPinIcon, CalendarIcon, CheckCircle2Icon, ActivityIcon, GlobeIcon } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useWorkspace } from '../../store/workspaceStore';
import { STAGE_META, LISTING_PLATFORMS } from '../../types';
import { Avatar } from '../common/Avatar';
import { ChecklistRow } from './ChecklistRow';
import { Topbar } from '../layout/Topbar';
export function ProposalDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const proposal = useWorkspace(s => s.proposals.find(p => p.id === id));
  const activity = useWorkspace(s => s.activity.filter(a => a.proposalId === id));
  const property = useWorkspace(s => s.properties.find(p => p.name === proposal?.property));
  const togglePlatform = useWorkspace(s => s.togglePropertyPlatform);
  const personById = useWorkspace(s => s.personById);
  if (!proposal) {
    return <div className="flex flex-1 items-center justify-center text-slate-400" data-id="element-155" data-mp="2oa94qx2a4ack">Request not found.</div>;
  }
  const broker = personById(proposal.requestedById);
  const done = proposal.checklist.filter(c => c.status === 'done').length;
  const total = proposal.checklist.length;
  const pct = Math.round(done / total * 100);
  const stage = STAGE_META[proposal.stage];
  const listingItem = proposal.checklist.find(c => c.key === 'listing');
  const showPlatforms = listingItem && listingItem.status !== 'not_started';
  return <div className="flex min-h-full flex-1 flex-col" data-id="element-156" data-mp="2f7zxcg2b4x3e">
      <Topbar title={proposal.property} subtitle={proposal.address} data-id="element-157" data-mp="2f9kqq2cvy9zo" />

      <div className="mp-scroll flex-1 overflow-y-auto p-6" data-id="element-158" data-mp="2afgjdhzpjzy2">
        <button onClick={() => navigate('/proposals')} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800" data-id="element-159" data-mp="1mqxbh3kklp6i">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-160" data-mp="27h1578dwes54" /> Back to requests
        </button>

        {/* Summary card */}
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-id="element-161" data-mp="2bhbbyn9ilml1">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-162" data-mp="2g8b9nuqgk8ol">
            <div data-id="element-163" data-mp="2l7wgerbvdlbv">
              <div className="mb-1 flex items-center gap-2" data-id="element-164" data-mp="2l8folgbpwvcp">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${stage.color}`} data-id="element-165" data-mp="2la0n1h15kyfu">{stage.label}</span>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600" data-id="element-166" data-mp="1sj8owe0j1zpv">{proposal.deckType}</span>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600" data-id="element-167" data-mp="1xha8r15s232e">{proposal.assetType}</span>
              </div>
              <h2 className="text-lg font-bold text-slate-900" data-id="element-168" data-mp="38xh2vk78f0pw">{proposal.property}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500" data-id="element-169" data-mp="38zl7k4cyg3r8">
                <span className="inline-flex items-center gap-1" data-id="element-170" data-mp="3dyel1ws8rure"><MapPinIcon className="h-4 w-4" data-id="element-171" data-mp="1286notoouluo" /> {proposal.address}</span>
                <span className="inline-flex items-center gap-1" data-id="element-172" data-mp="x9lngu9fs268"><CalendarIcon className="h-4 w-4" data-id="element-173" data-mp="xan91so2w727" /> Requested {format(new Date(proposal.requestedAt), 'MMM d, yyyy')}</span>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-174" data-mp="nl3rqrg0kl9g">
              <div className="text-right" data-id="element-175" data-mp="ni97azx030g1">
                <p className="text-xs text-slate-400" data-id="element-176" data-mp="jj0dxw5vh4l0">Requested by</p>
                <p className="text-sm font-semibold text-slate-700" data-id="element-177" data-mp="ejng6gdgqd3n">{broker?.name}</p>
              </div>
              <Avatar person={broker} size={40} data-id="element-178" data-mp="9kkixi3q5b68" />
            </div>
          </div>

          {/* progress */}
          <div className="mt-5" data-id="element-179" data-mp="4lzl1pby33ky">
            <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-slate-500" data-id="element-180" data-mp="4mrfuxxpwxa6">
              <span className="inline-flex items-center gap-1" data-id="element-181" data-mp="23y21eqp9vhue"><CheckCircle2Icon className="h-4 w-4 text-emerald-500" data-id="element-182" data-mp="1yzqryh2w39cp" /> {done} of {total} complete</span>
              <span data-id="element-183" data-mp="270gcwsv1859n">{pct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" data-id="element-184" data-mp="270q4ulzn0mhz">
              <motion.div className="h-full rounded-full bg-emerald-500" initial={{
              width: 0
            }} animate={{
              width: `${pct}%`
            }} transition={{
              duration: 0.5
            }} data-id="element-185" data-mp="2bxyn0mkscqj8" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]" data-id="element-186" data-mp="1o9ffwadh2jsq">
          {/* Checklist */}
          <div className="space-y-2.5" data-id="element-187" data-mp="1t1gwg8hoawok">
            <h3 className="px-1 text-sm font-bold uppercase tracking-wide text-slate-500" data-id="element-188" data-mp="1t2iamltzjzai">Deal Checklist</h3>
            {proposal.checklist.map(item => <ChecklistRow key={item.id} proposalId={proposal.id} item={item} data-id="element-189" data-mp="1jbnkh6dmcfjs" />)}

            {/* Listing platforms tracker */}
            {showPlatforms && property && <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-id="element-190" data-mp="19coclq0ognxu">
                <h4 className="mb-1 flex items-center gap-1.5 text-sm font-bold text-slate-800" data-id="element-191" data-mp="qnm851zi6gvq">
                  <GlobeIcon className="h-4 w-4 text-cyan-500" data-id="element-192" data-mp="lpkq8kdtcnlu" /> Listed On
                </h4>
                <p className="mb-3 text-xs text-slate-400" data-id="element-193" data-mp="zokh7yostel6">Track every platform this property is listed on.</p>
                <div className="flex flex-wrap gap-2" data-id="element-194" data-mp="up7q31qw9lcs">
                  {LISTING_PLATFORMS.map(plat => {
                const active = property.platforms.includes(plat);
                return <button key={plat} onClick={() => togglePlatform(property.id, plat)} className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${active ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`} data-id="element-195" data-mp="14mub85yse957">
                        {plat}
                      </button>;
              })}
                </div>
              </div>}
          </div>

          {/* Activity feed */}
          <aside className="lg:sticky lg:top-6" data-id="element-196" data-mp="u1f0elqnrztb">
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-id="element-197" data-mp="tzkeb43ni1nn">
              <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-800" data-id="element-198" data-mp="ytyerkx66yz6">
                <ActivityIcon className="h-4 w-4 text-brand-accent" data-id="element-199" data-mp="13qyktvu0s1qa" /> Activity
              </h3>
              <div className="mp-scroll max-h-[520px] space-y-4 overflow-y-auto pr-1" data-id="element-200" data-mp="18p8jwhiv7mh6">
                {activity.length === 0 && <p className="text-sm text-slate-400" data-id="element-201" data-mp="1csy1uo51b5t8">No activity yet.</p>}
                {activity.map(a => {
                const author = personById(a.authorId);
                return <div key={a.id} className="flex gap-2.5" data-id="element-202" data-mp="3elchfgk3vy6h">
                      <Avatar person={author} size={28} data-id="element-203" data-mp="3jjns1ti86cpr" />
                      <div className="min-w-0" data-id="element-204" data-mp="3jkfm2zw6c2dx">
                        <p className="text-sm text-slate-700" data-id="element-205" data-mp="3c04zown3d74i">
                          <span className="font-semibold" data-id="element-206" data-mp="378n0ad0pveln">{author?.name}</span> {a.text}
                        </p>
                        <p className="text-[11px] text-slate-400" data-id="element-207" data-mp="328ijelvwchuw">{formatDistanceToNow(new Date(a.createdAt), {
                        addSuffix: true
                      })}</p>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>;
}